import * as React from "react";
import usePagination from "@mui/material/usePagination";
import { styled } from "@mui/material/styles";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useOrganisation } from "../../../hooks/useOrganisation";
import { getTextColor } from "../../../helpers";

const List = styled("ul")({
  listStyle: "none",
  padding: 0,
  margin: 0,
  display: "flex",
});

export default function Pagination({
  count,
  setCurrentPage,
  currentPage,
  showPageSize = true, // New prop to control visibility
  pageSize = 10, // Current page size
  setPageSize, // Function to update page size
  totalItems = 0, // Total number of items for display
  disabled = false, // For loading states
}) {
  // Use a ref to prevent losing our current page state
  const currentPageRef = React.useRef(currentPage);

  // Update ref when prop changes
  React.useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  // Handle page changes
  const handlePageChange = (event, page) => {
    // Update both the ref and the state through the parent component
    currentPageRef.current = page;
    setCurrentPage(page);

    // For debugging
    console.log(`Pagination component: changing to page ${page}`);
  };

  // Handle page size change
  const handlePageSizeChange = (event) => {
    const newPageSize = event.target.value;
    if (setPageSize) {
      setPageSize(newPageSize);
      // Reset to first page when changing page size
      setCurrentPage(1);
    }
  };

  const { items } = usePagination({
    count: count,
    page: currentPage, // Use the current page from props
    onChange: handlePageChange,
    boundaryCount: 1, // Ensures we always show first/last pages
    siblingCount: 1, // Show one sibling on each side of current page
    disabled: disabled,
  });

  const { primaryColor } = useOrganisation();
  const textColor = getTextColor(primaryColor) || "black";

  // Calculate the range of items being displayed
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 2,
        mt: 2,
      }}
    >
      {/* Page Size Selector */}
      {showPageSize && setPageSize && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="page-size-label">Show</InputLabel>
            <Select
              labelId="page-size-label"
              value={pageSize}
              label="Show"
              onChange={handlePageSizeChange}
              disabled={disabled}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
          {totalItems > 0 && (
            <Typography variant="body2" color="text.secondary">
              Showing {startItem}-{endItem} of {totalItems} items
            </Typography>
          )}
        </Box>
      )}

      {/* Pagination Controls */}
      <nav>
        <List>
          {items.map(({ page, type, selected, ...item }, index) => {
            let children = null;

            if (type === "start-ellipsis" || type === "end-ellipsis") {
              children = "…";
            } else if (type === "page") {
              children = (
                <button
                  type="button"
                  style={{
                    fontWeight: selected ? "bold" : undefined,
                    background: selected ? primaryColor : "none",
                    color: selected ? textColor : "black",
                    borderRadius: 8,
                    marginRight: 4,
                    border: "none",
                    cursor: disabled ? "default" : "pointer",
                    padding: "4px 8px", // Add some padding for better click target
                    minWidth: "32px", // Ensure consistent width
                    opacity: disabled ? 0.5 : 1,
                  }}
                  disabled={disabled}
                  {...item}
                >
                  {page}
                </button>
              );
            } else if (type === "previous" || type === "next") {
              // Use the individual button's disabled state from usePagination
              const isButtonDisabled = item.disabled || disabled;

              children = (
                <button
                  type="button"
                  {...item}
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor: isButtonDisabled ? "default" : "pointer",
                    opacity: isButtonDisabled ? 0.5 : 1,
                    padding: "4px 8px",
                  }}
                  disabled={isButtonDisabled}
                >
                  {type === "previous" ? "« Prev" : "Next »"}
                </button>
              );
            } else {
              children = (
                <button
                  type="button"
                  {...item}
                  style={{
                    cursor: item.disabled || disabled ? "default" : "pointer",
                    padding: "4px 8px",
                    opacity: item.disabled || disabled ? 0.5 : 1,
                  }}
                  disabled={item.disabled || disabled}
                >
                  {type}
                </button>
              );
            }

            return <li key={index}>{children}</li>;
          })}
        </List>
      </nav>
    </Box>
  );
}
